import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BrandBand from './components/BrandBand';
import './SuperAdminDashboard.css';

const PAGE_SIZE = 25;

const REFUSAL_MESSAGES = {
  env_managed: 'This user is a Super Admin through server configuration and cannot be demoted here.',
  self: 'You cannot remove your own Super Admin access.',
  last_admin: 'This is the last Super Admin, so it cannot be demoted.',
  not_authorized: 'You are not authorized to change Super Admin access.',
  unknown_user: 'That user was not found. Refresh the page and try again.',
  invalid_request: 'The request was not valid, so no change was made.',
};

const ACTION_LABELS = {
  login: 'Signed in',
  bot_add: 'Bot added',
  bot_remove: 'Bot removed',
  bot_repair: 'Bot repaired',
  access_mode_update: 'Access mode changed',
  permission_grant: 'Permission granted',
  permission_revoke: 'Permission revoked',
  super_admin_promote: 'Promoted to Super Admin',
  super_admin_demote: 'Demoted from Super Admin',
};

const actionLabel = (action) =>
  ACTION_LABELS[action] || String(action || 'Unknown action').replace(/_/g, ' ');

// Timestamps are UTC; render as "YYYY-MM-DD HH:MM UTC".
const formatTimestamp = (value) => {
  if (!value) return '';
  const text = String(value).replace(' ', 'T');
  const date = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(text) ? text : `${text}Z`);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

const refusalMessage = (error) => {
  const data = error.response?.data;
  if (data?.outcome && REFUSAL_MESSAGES[data.outcome]) return REFUSAL_MESSAGES[data.outcome];
  return 'The change could not be completed. No change was made.';
};

function UsersSection({ currentUserId, onChanged }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/v1/admin/users')
      .then((response) => {
        if (!cancelled) setUsers(response.data?.users || []);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load users. Try again later.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const setAdmin = async (target, makeAdmin) => {
    setBusyId(target.id);
    setActionError('');
    try {
      const response = await axios.post(
        `/v1/admin/users/${encodeURIComponent(target.id)}/super-admin`,
        { is_super_admin: makeAdmin }
      );
      const confirmed = response.data?.is_super_admin;
      setUsers((prev) => prev.map((u) => (
        u.id === target.id ? { ...u, is_super_admin: typeof confirmed === 'boolean' ? confirmed : makeAdmin } : u
      )));
      if (onChanged) onChanged();
    } catch (error) {
      setActionError(refusalMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="ui-section" aria-labelledby="users-heading">
      <h2 id="users-heading" className="admin-heading">Users</h2>
      {actionError && <div className="ui-alert ui-alert--error" role="alert">{actionError}</div>}
      {loading && (
        <div className="ui-empty" role="status">
          <span className="ui-spinner" aria-hidden="true"></span>
          <p>Loading users...</p>
        </div>
      )}
      {loadError && <div className="ui-alert ui-alert--error" role="alert">{loadError}</div>}
      {!loading && !loadError && users.length === 0 && (
        <div className="ui-empty ui-prose">No users have signed in yet.</div>
      )}
      {!loading && users.length > 0 && (
        <ul className="ui-card admin-list">
          {users.map((u) => {
            const isSelf = currentUserId != null && String(u.id) === String(currentUserId);
            const label = u.name || u.id;
            return (
              <li key={u.id} className="admin-row">
                <div className="admin-row-main">
                  <span className="group-name">{label}</span>
                  {isSelf && <span className="admin-tag">You</span>}
                  {u.is_super_admin && <span className="admin-tag">Super Admin</span>}
                  {u.env_managed && <span className="admin-tag">env-managed</span>}
                  <div className="admin-meta">
                    {u.name ? `${u.id} · ` : ''}Last sign-in {u.last_login ? formatTimestamp(u.last_login) : 'never'}
                  </div>
                </div>
                {!u.is_super_admin && (
                  <button
                    type="button"
                    className="ui-button ui-button--primary"
                    disabled={busyId === u.id}
                    aria-label={`Promote ${label} to Super Admin`}
                    onClick={() => setAdmin(u, true)}
                  >
                    Promote
                  </button>
                )}
                {u.is_super_admin && !u.env_managed && !isSelf && (
                  <button
                    type="button"
                    className="ui-button ui-button--outline-danger"
                    disabled={busyId === u.id}
                    aria-label={`Demote ${label} from Super Admin`}
                    onClick={() => setAdmin(u, false)}
                  >
                    Demote
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function AuditSection({ refreshKey }) {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get('/v1/admin/audit-events', { params: { limit: PAGE_SIZE } })
      .then((response) => {
        if (cancelled) return;
        setEvents(response.data?.events || []);
        setCursor(response.data?.next_cursor ?? null);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load recent activity. Try again later.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const loadMore = async () => {
    setLoadingMore(true);
    setError('');
    try {
      const response = await axios.get('/v1/admin/audit-events', {
        params: { limit: PAGE_SIZE, before_id: cursor },
      });
      setEvents((prev) => [...prev, ...(response.data?.events || [])]);
      setCursor(response.data?.next_cursor ?? null);
    } catch (e) {
      setError('Could not load more activity. Try again later.');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="ui-section" aria-labelledby="audit-heading">
      <h2 id="audit-heading" className="admin-heading">Recent activity</h2>
      {loading && (
        <div className="ui-empty" role="status">
          <span className="ui-spinner" aria-hidden="true"></span>
          <p>Loading activity...</p>
        </div>
      )}
      {error && <div className="ui-alert ui-alert--error" role="alert">{error}</div>}
      {!loading && !error && events.length === 0 && (
        <div className="ui-empty ui-prose">No activity has been recorded yet.</div>
      )}
      {!loading && events.length > 0 && (
        <ul className="ui-card admin-list">
          {events.map((ev) => (
            <li key={ev.id} className="admin-row">
              <div className="admin-row-main">
                <span className="group-name">{actionLabel(ev.action)}</span>
                {!ev.success && <span className="admin-tag admin-tag--failed">Failed</span>}
                <div className="admin-meta">
                  By {ev.actor_name || ev.actor_user_id || 'unknown'}
                  {ev.target_user_id || ev.target_name
                    ? ` · Target ${ev.target_name || ev.target_user_id}`
                    : ''}
                </div>
              </div>
              <time className="admin-meta" dateTime={ev.timestamp}>
                {formatTimestamp(ev.timestamp)}
              </time>
            </li>
          ))}
        </ul>
      )}
      {!loading && cursor !== null && (
        <button
          type="button"
          className="ui-button ui-button--secondary"
          disabled={loadingMore}
          onClick={loadMore}
        >
          {loadingMore ? 'Loading...' : 'Load more'}
        </button>
      )}
    </section>
  );
}

function SuperAdminDashboard({ user, onLogout }) {
  const [auditRefresh, setAuditRefresh] = useState(0);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllGroups();
  }, []);

  const fetchAllGroups = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/v1/admin/all-groups');
      if (response.data.success) {
        setAllGroups(response.data.groups);
      }
    } catch (error) {
      console.error('Failed to fetch all groups:', error);
      if (error.response?.status === 403) {
        setError('Super admin access required');
      } else {
        setError(error.response?.data?.error || 'Failed to load groups data');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = allGroups.filter(group => 
    group.group_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.group_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.owner_user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.bot_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log('Copied to clipboard:', text);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <>
        <BrandBand userName={user.name} onLogout={onLogout} />
        <div className="ui-page">
          <div className="ui-empty" role="status">
            <span className="ui-spinner" aria-hidden="true"></span>
            <p>Loading all groups...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <BrandBand userName={user.name} onLogout={onLogout} />
        <div className="ui-page ui-section">
          <div className="ui-alert ui-alert--error" role="alert">{error}</div>
          <button className="ui-button ui-button--secondary" onClick={() => window.location.href = '/'}>← Back to Dashboard</button>
        </div>
      </>
    );
  }

  return (
    <>
      <BrandBand userName={user.name} onLogout={onLogout} />
      <div className="ui-page">
        <div className="ui-header">
          <h1 className="super-admin-title">Super Admin Dashboard</h1>
          <div className="ui-cluster">
            <button onClick={() => window.location.href = '/'} className="ui-button ui-button--secondary">
              ← Dashboard
            </button>
          </div>
        </div>

        <div className="ui-section">
          <div className="ui-grid">
            <div className="ui-card stat-card">
              <h3 className="ui-muted">Total Groups</h3>
              <div className="stat-number">{allGroups.length}</div>
            </div>
            <div className="ui-card stat-card">
              <h3 className="ui-muted">Active Bots</h3>
              <div className="stat-number">{allGroups.filter(g => g.bot_id).length}</div>
            </div>
            <div className="ui-card stat-card">
              <h3 className="ui-muted">Total Members</h3>
              <div className="stat-number">{allGroups.reduce((sum, g) => sum + (g.member_count || 0), 0)}</div>
            </div>
          </div>

          <div className="ui-cluster">
            <input
              type="text"
              placeholder="Search groups, IDs, owners, or bot IDs..."
              aria-label="Search groups"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ui-grow search-input"
            />
            <button onClick={() => setSearchTerm('')} className="ui-button ui-button--secondary">
              Clear
            </button>
          </div>

          <div className="ui-card groups-table-scroll">
            <table className="groups-table">
              <thead>
                <tr>
                  <th>Group Name</th>
                  <th>Group ID</th>
                  <th>Owner</th>
                  <th>Bot ID</th>
                  <th>Access Mode</th>
                  <th>Members</th>
                  <th>Permissions</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map(group => (
                  <tr key={group.group_id}>
                    <td className="group-name">{group.group_name}</td>
                    <td 
                      className="clickable-id" 
                      onClick={() => copyToClipboard(group.group_id)}
                      title="Click to copy Group ID"
                    >
                      {group.group_id}
                    </td>
                    <td 
                      className="clickable-id" 
                      onClick={() => copyToClipboard(group.owner_user_id)}
                      title="Click to copy Owner ID"
                    >
                      {group.owner_user_id}
                    </td>
                    <td>
                      {group.bot_id ? (
                        <span 
                          className="clickable-id bot-active" 
                          onClick={() => copyToClipboard(group.bot_id)}
                          title="Click to copy Bot ID"
                        >
                          {group.bot_id}
                        </span>
                      ) : (
                        <span className="no-bot">No Bot</span>
                      )}
                    </td>
                    <td className="access-mode">{group.access_mode?.replace('_', ' ')}</td>
                    <td className="member-count">{group.member_count || 0}</td>
                    <td className="permission-count">{group.permission_count || 0}</td>
                    <td className="created-date">
                      {new Date(group.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredGroups.length === 0 && searchTerm && (
            <div className="ui-empty ui-prose">
              No groups found matching "{searchTerm}"
            </div>
          )}
        </div>

        <UsersSection
          currentUserId={user.user_id || user.id}
          onChanged={() => setAuditRefresh((n) => n + 1)}
        />
        <AuditSection refreshKey={auditRefresh} />
      </div>
    </>
  );
}

export default SuperAdminDashboard;